import { isAllPagesOnOnePage } from '@webapp/common/lib/const';
import { partsToSeconds } from '@webapp/common/lib/date';
import { questionsWithForwardLogicAnswers } from '@webapp/common/lib/forward-logic';
import { isEmbedded } from '@webapp/common/lib/ua';
import type { PageModel, QuestionLogicModel } from '@webapp/common/resources/entities';
import { BundleType } from '@webapp/common/resources/entities';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import { checkIsQWithClickReply, QuestionType } from '@webapp/common/resources/survey';

import { BundleEntity } from '@webapp/survey/src/entities/bundle';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';

import { withRandomPages } from '../guards';
import {
    getQ,
    loadBundlesOrder,
    loadPagesOrder,
    loadTimer,
    shufflePages,
    storeBundlesOrder,
    storePagesOrder,
} from '../lib';

// import { filterBundlesQuestionsByLogic } from './logic';
import { filterBundlesQuestionsByLogicNew } from './logic-new';
export const shuffleQuestions = <T extends QuestionActorRef>(answers: Array<T>): Array<T> => {
    const fixedAnswers = answers.map((p) => (p.getSnapshot().context.pinned ? p : null));
    const shuffledAnswers = answers.filter((p) => !p.getSnapshot().context.pinned);
    if (shuffledAnswers.length === 0) return fixedAnswers;

    for (let i = shuffledAnswers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledAnswers[i], shuffledAnswers[j]] = [shuffledAnswers[j], shuffledAnswers[i]];
    }
    let i = 0;
    return fixedAnswers.map((p) => p || shuffledAnswers[i++]);
};

const randomizePage = (page: PageModel, pageQuestions: Array<QuestionActorRef>): Array<QuestionActorRef> => {
    // TODO ?? what this for
    const pickOrdered = (ids: Array<number>): Array<QuestionActorRef> =>
        ids.map((id, idx) => {
            const q = pageQuestions.find((q) => getQ(q).id === id);
            if (q) {
                q.send({ type: 'SET_ORDER', value: idx + 1 });
            }
            // FIXME?
            return q;
        });

    if (page.shuffleOrder.length > 0) {
        return pickOrdered(page.shuffleOrder);
    }

    const alwaysLast = pageQuestions.filter((q) => QuestionType.AGREEMENT === getQ(q).type);
    const regular = pageQuestions.filter((q) => QuestionType.AGREEMENT !== getQ(q).type);

    const ordered = shuffleQuestions(regular).concat(alwaysLast);
    ordered.forEach((q, idx) => q.send({ type: 'SET_ORDER', value: idx + 1 }));
    page.shuffleOrder = ordered.map((o) => parseFloat(o.getSnapshot().context.id.toString()));

    return ordered;
};

const packOneByOne = (
    page: PageModel,
    pageQuestions: Array<QuestionActorRef>,
    bundles: Array<BundleEntity>,
): Array<BundleEntity> => {
    let questions = pageQuestions;

    if (page.random) {
        questions = randomizePage(page, pageQuestions);
    }

    questions.forEach((question) => {
        bundles.push(
            new BundleEntity(
                {
                    type: BundleType.QUESTIONS,
                    random: page.random,
                    page,
                },
                [question],
            ),
        );
    });

    return bundles;
};

const packRandom = (page: PageModel, pageQuestions: Array<QuestionActorRef>, bundles: Array<BundleEntity>) => {
    const questions = randomizePage(page, pageQuestions);

    bundles.push(
        new BundleEntity(
            {
                random: true,
                type: BundleType.QUESTIONS,
                page,
            },
            questions,
        ),
    );

    return bundles;
};

const packOrdinary = (
    page: PageModel,
    pageQuestions: Array<QuestionActorRef>,
    bundles: Array<BundleEntity>,
    clickReplyOn: boolean,
) => {
    let split = true;
    let prevClickToReply = false;

    pageQuestions.forEach((q) => {
        const clickReply = clickReplyOn && checkIsQWithClickReply(getQ(q));

        if (clickReply || split || prevClickToReply) {
            bundles.push(
                new BundleEntity(
                    {
                        type: BundleType.QUESTIONS,
                        page,
                    },
                    [q],
                ),
            );

            if (split) {
                split = false;
            }
        } else {
            bundles[bundles.length - 1].questions.push(q);
        }

        prevClickToReply = clickReply;
    });

    return bundles;
};

export const packBundles = (
    pages: Array<PageModel>,
    questions: Array<QuestionActorRef>,
    clickReplyOn: boolean,
    globalLogic: Array<QuestionLogicModel>,
    queryParams: Record<string, string>,
    linkParams: Record<string, string>,
    extendedLogic: Array<QuestionLogicModel>,
) => {
    let bundles = pages.reduce((bundles, page) => {
        const pageQuestions = questions
            .filter((q) => getQ(q).page === page.id)
            .filter((qActor) => {
                const q = getQ(qActor);

                if (!questionsWithForwardLogicAnswers.has(q.type) || q.type === QuestionType.TEXT_PERCEPTION) {
                    return true;
                }

                return q.answersActors.some((a) => !a.getSnapshot().context.hidden);
            });

        if (page.onebyone || (clickReplyOn && !isEmbedded)) {
            return packOneByOne(page, pageQuestions, bundles);
        } else if (page.random) {
            return packRandom(page, pageQuestions, bundles);
        } else {
            return packOrdinary(page, pageQuestions, bundles, clickReplyOn);
        }
    }, [] as Array<BundleEntity>);
    filterBundlesQuestionsByLogicNew(bundles, globalLogic, queryParams, linkParams, extendedLogic);
    // второй раз чтобы adjust корректно применился
    const { newBundles, hiddenAnswers } = filterBundlesQuestionsByLogicNew(
        bundles,
        globalLogic,
        queryParams,
        linkParams,
        extendedLogic,
    );

    bundles = newBundles;

    if (isAllPagesOnOnePage) {
        if (bundles.length === 0) {
            // Раньше падало на bundles[0].questions = ... (FRONT-12QZ):
            // страницы есть, а бандлы не собрались (всё отфильтровано логикой).
            console.error('packBundles: empty bundles for one-page survey', {
                pages: pages.length,
                questions: questions.length,
            });
        } else {
            bundles[0].questions = bundles.flatMap((b) => b.questions);
            const lastBundle = bundles[bundles.length - 1];
            bundles = [bundles[0]];
            if (lastBundle.type !== BundleType.QUESTIONS) {
                bundles.push(lastBundle);
            }
        }
    }

    return { bundles, hiddenAnswers };
};

export const splitByTimerOn = (timerOn: number, bundles: Array<BundleEntity>): Array<BundleEntity> => {
    const bundleWithTimerOnQuestion = bundles.find(({ questions }) => questions.find((q) => getQ(q).id === timerOn));

    if (!bundleWithTimerOnQuestion) {
        console.error(`splitByTimerOn: question ${JSON.stringify(timerOn)} no found`);
        return bundles; // skip for non-existed question
    }

    const { questions } = bundleWithTimerOnQuestion;

    if (questions.length < 2) {
        return bundles;
    }

    const newBundles: Array<BundleEntity> = [];
    for (const b of bundles) {
        if (b !== bundleWithTimerOnQuestion) {
            newBundles.push(b);
            continue;
        }

        const timerQuestionIdx = questions.findIndex((q) => getQ(q).id === timerOn);

        // @ts-ignore
        newBundles.push({
            ...bundleWithTimerOnQuestion,
            questions: questions && (questions.slice(0, timerQuestionIdx + 1) as any),
        });

        if (questions[timerQuestionIdx + 1]) {
            // FIXME: types
            // @ts-ignore
            newBundles.push({
                ...bundleWithTimerOnQuestion,
                questions: questions.slice(timerQuestionIdx + 1) as any,
            });
        }
    }

    return newBundles;
};

export const splitByQuestionsWithTimer = (bundles: Array<BundleEntity>): Array<BundleEntity> => {
    const newBundles: Array<BundleEntity> = [];

    const add = (page, questions): void => {
        newBundles.push(
            new BundleEntity(
                {
                    type: BundleType.QUESTIONS,
                    page,
                },
                questions,
            ),
        );
    };

    for (const b of bundles) {
        const { page } = b;
        let { questions } = b;
        let questionWithTimerIdx = questions.findIndex(
            (q) => !!(getQ(q).params.timer || getQ(q).params.testAnswerReaction),
        );

        if (questionWithTimerIdx < 0) {
            newBundles.push(b);
            continue;
        }

        while (
            (questionWithTimerIdx = questions.findIndex(
                (q) => !!(getQ(q).params.timer || getQ(q).params.testAnswerReaction),
            )) > -1
        ) {
            const questionsBefore = questions.slice(0, questionWithTimerIdx);
            const questionWithTimer = questions.slice(questionWithTimerIdx, questionWithTimerIdx + 1);

            questions = questions.slice(questionWithTimerIdx + 1) as any;

            if (questionsBefore.length > 0) {
                add(page, questionsBefore);
            }

            add(page, questionWithTimer);
        }

        if (questions.length > 0) {
            add(page, questions);
        }
    }

    return newBundles;
};

export const calcCurrentBundleIndex = (bundles: Array<BundleEntity>): number => {
    let currentBundleIndex = 0;
    for (const { questions } of bundles) {
        const validAndAnswered = questions.every((q) => getQ(q).dirty && !getQ(q).invalid);
        const timers = questions
            .map((q) => {
                const timerId = `question-timer-${q.getSnapshot().context.id}`;
                return {
                    q,
                    timer: loadTimer(timerId),
                };
            })
            .filter((s) => Boolean(s.timer));
        const isAnyOfTimersExpired = timers.some(({ q, timer }) => {
            const context = q.getSnapshot().context;
            const duration = partsToSeconds(context.params.hours, context.params.minutes, context.params.seconds);
            return timer + duration * 1000 < Date.now();
        });
        if (isAnyOfTimersExpired || validAndAnswered) {
            currentBundleIndex++;
        } else {
            break;
        }
    }

    const finalBundleIdx = Math.min(currentBundleIndex, bundles.length - 1);
    const finalBundle = bundles[finalBundleIdx];
    if (finalBundle?.type !== BundleType.QUESTIONS) {
        return Math.max(finalBundleIdx - 1, 0);
    }
    return finalBundleIdx;
};

const sortBundleQuestions = (bundles: Array<BundleEntity>): Array<BundleEntity> => {
    return bundles.map((bundle) => {
        bundle.questions = bundle.questions.slice().sort((a, b) => getQ(a).order - getQ(b).order);
        return bundle;
    });
};

export const prepareBundles = (
    domain: string,
    info: InfoModel,
    pagesRaw: Array<PageModel>,
    questions: Array<QuestionActorRef>,
    calcIndex: boolean,
    clickReplyOn: boolean,
    globalLogic: Array<QuestionLogicModel>,
    queryParams: Record<string, string>,
    linkParams: Record<string, string>,
    extendedLogic: Array<QuestionLogicModel>,
): { bundles: Array<BundleEntity>; currentIndex: number; hiddenAnswers: Record<number, Array<number>> } => {
    const { timer, timerOn } = info.params.main;
    let bundles: Array<BundleEntity>;
    let currentIndex;
    let pages = pagesRaw;
    const isShufflePagesEnabled = pages.some((p) => p.randomFixed);

    if (isShufflePagesEnabled) {
        const loadedPages = loadPagesOrder(domain, pagesRaw);
        if (loadedPages !== pagesRaw) {
            pages = loadedPages;
        } else {
            pages = shufflePages(pagesRaw);
        }
    }
    const { bundles: newBundles, hiddenAnswers } = packBundles(
        pages,
        questions,
        clickReplyOn,
        globalLogic,
        queryParams,
        linkParams,
        extendedLogic,
    );

    bundles = newBundles;

    if (timerOn && timer && !isAllPagesOnOnePage) {
        bundles = splitByTimerOn(timerOn, bundles);
    }

    if (!isAllPagesOnOnePage) {
        bundles = splitByQuestionsWithTimer(bundles);
    }
    if (withRandomPages(pages)) {
        bundles = loadBundlesOrder(domain, bundles);
    }

    bundles = sortBundleQuestions(bundles);

    if (withRandomPages(pages)) {
        setTimeout(() => {
            // потому что order выше проставляется асинхронно
            storeBundlesOrder(domain, bundles);
        }, 100);
    }

    if (isShufflePagesEnabled) {
        storePagesOrder(domain, pages);
    }

    if (calcIndex) {
        // calc index used on init, afterward index incremented or decremented
        // this actually only used on load saved answers and can be store along with data
        currentIndex = calcCurrentBundleIndex(bundles);
    }

    return { bundles, currentIndex, hiddenAnswers };
};
